import { useEffect, useRef, useState } from 'react';

type Props = {
  message?: string;
};

const Loader = ({ message }: Props) => {
  const loadingMessage = useRef<HTMLParagraphElement>(null);
  const [changingMessage, setChangingMessage] = useState(message);

  useEffect(() => {
    // CHANGE MESSAGE AFTER 2 SECONDS
    const timeout = setTimeout(() => {
      if (loadingMessage.current && changingMessage !== 'Rendering') {
        loadingMessage.current.classList.add('opacity-0');
        setTimeout(() => {
          loadingMessage.current?.classList.remove('opacity-0');
          setChangingMessage('Rendering');
        }, 200);
      }
    }, 2000);

    // CHANGE TAB TITLE
    document.title = 'Loading model.';

    const titleInterval = setInterval(() => {
      if (document.title === 'Loading model.') {
        document.title = 'Loading model..';
      } else if (document.title === 'Loading model..') {
        document.title = 'Loading model...';
      } else {
        document.title = 'Loading model.';
      }
    }, 300);

    // Cleanup intervals on unmount
    return () => {
      clearTimeout(timeout);
      clearInterval(titleInterval);
      document.title = 'DaddyCad';
    };
  }, [changingMessage]);

  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#0a0a15] via-[#0f0f1f] to-[#0a0a15]">
      {/* Animated background gradient orbs */}
      <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-cyan-500/30 to-blue-500/30 blur-3xl animate-pulse" style={{ top: '10%', left: '10%', animationDuration: '3s' }} />
      <div className="absolute w-80 h-80 rounded-full bg-gradient-to-r from-purple-500/25 to-pink-500/25 blur-3xl animate-pulse" style={{ bottom: '10%', right: '10%', animationDuration: '4s', animationDelay: '1s' }} />
      <div className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-blue-600/20 to-cyan-400/20 blur-2xl animate-pulse" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', animationDuration: '2.5s', animationDelay: '0.5s' }} />

      {/* Animated grid lines */}
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(rgba(0,212,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.1) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />

      {/* Main content */}
      <div className="relative flex flex-col items-center gap-8 z-10">
        {/* Animated orbital rings */}
        <div className="relative w-32 h-32">
          {/* Outer ring */}
          <div className="absolute inset-0 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00D4FF', borderRightColor: '#00D4FF40', animationDuration: '3s' }} />
          {/* Middle ring */}
          <div className="absolute inset-3 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#8B5CF6', borderLeftColor: '#8B5CF640', animationDuration: '2s', animationDirection: 'reverse' }} />
          {/* Inner ring */}
          <div className="absolute inset-6 rounded-full border-2 border-transparent animate-spin" style={{ borderBottomColor: '#EC4899', borderRightColor: '#EC489940', animationDuration: '1.5s' }} />

          {/* Center pulsing core */}
          <div className="absolute inset-10 rounded-full bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 animate-pulse shadow-lg shadow-cyan-500/50" />

          {/* Glow effect */}
          <div className="absolute inset-8 rounded-full bg-gradient-to-br from-cyan-400/40 via-purple-400/40 to-pink-400/40 blur-xl animate-pulse" style={{ animationDuration: '2s' }} />
        </div>

        {/* Text content */}
        <div className="flex flex-col items-center gap-2">
          {message && (
            <p
              ref={loadingMessage}
              className="text-lg font-semibold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-pulse transition-opacity duration-200"
            >
              {changingMessage}
            </p>
          )}
          <p className="text-sm text-zinc-400">
            {changingMessage === 'Rendering'
              ? 'Transforming your idea into 3D...'
              : 'Creating your design...'}
          </p>

          {/* Animated dots */}
          <div className="flex gap-1.5 mt-2">
            <div className="w-2 h-2 rounded-full bg-cyan-500 animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>

      {/* Corner decorations */}
      <div className="absolute top-6 left-6 w-16 h-16 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-lg" />
      <div className="absolute top-6 right-6 w-16 h-16 border-r-2 border-t-2 border-purple-500/30 rounded-tr-lg" />
      <div className="absolute bottom-6 left-6 w-16 h-16 border-l-2 border-b-2 border-pink-500/30 rounded-bl-lg" />
      <div className="absolute bottom-6 right-6 w-16 h-16 border-r-2 border-b-2 border-cyan-500/30 rounded-br-lg" />
    </div>
  );
};

export default Loader;
